import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: '' // Selected role
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // Check if user was redirected from registration
  useEffect(() => {
    if (location.state?.registrationSuccess) {
      setSuccessMessage('Registration successful! Please sign in with your credentials.');
    }
  }, [location]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Handle role selection
  const handleRoleSelect = (selectedRole) => {
    setFormData({
      ...formData,
      role: selectedRole
    });
    setError(''); // Clear any previous errors when role is selected
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate role selection
    if (!formData.role) {
      setError('Please select your role to continue');
      return;
    }
    
    setLoading(true);
    
    try {
      const user = await login(formData.email, formData.password, formData.role);
      
      // Redirect based on role
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'lecturer' || user.role === 'staff') {
        navigate('/lecturer-dashboard');
      } else if (user.role === 'student') {
        navigate('/student-dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid credentials or incorrect role. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Role options
  const roleOptions = [
    {
      key: 'admin',
      title: 'Administrator',
      color: '#F04915'
    },
    {
      key: 'lecturer',
      title: 'Lecturer/Staff',
      color: '#F04915'
    },
    {
      key: 'student',
      title: 'Student',
      color: '#F04915'
    }
  ];

  return (
    <div style={{
      width: '100%',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
      background: 'linear-gradient(135deg, #F5F7FA 0%, #E8F4FD 50%, #F5F7FA 100%)',
      position: 'relative'
    }}>
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          radial-gradient(circle at 20% 80%, rgba(240, 73, 21, 0.08) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(240, 73, 21, 0.06) 0%, transparent 50%),
          radial-gradient(circle at 40% 40%, rgba(240, 73, 21, 0.04) 0%, transparent 50%),
          radial-gradient(circle at 60% 70%, rgba(240, 73, 21, 0.05) 0%, transparent 50%)
        `,
        zIndex: 0
      }}>
      </div>

      {/* Left Side - Image and Branding */}
      <div style={{
        flex: window.innerWidth <= 768 ? 'none' : '1',
        height: window.innerWidth <= 768 ? '35vh' : '100vh',
        minHeight: window.innerWidth <= 768 ? '280px' : '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: window.innerWidth <= 768 ? '20px 15px' : '40px 20px',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Main Logo Image */}
        <div style={{
          background: `url('/assets/orangeatt.png') center/contain no-repeat`,
          width: window.innerWidth <= 480 ? '220px' : window.innerWidth <= 768 ? '260px' : '400px',
          height: window.innerWidth <= 480 ? '90px' : window.innerWidth <= 768 ? '110px' : '180px',
          marginBottom: window.innerWidth <= 768 ? '15px' : '30px',
          filter: 'drop-shadow(0 10px 30px rgba(240, 73, 21, 0.2))'
        }}>
        </div>

        {/* Powered By Text */}
        <div style={{
          textAlign: 'center',
          color: '#666',
          fontSize: window.innerWidth <= 480 ? '12px' : '14px',
          fontFamily: 'Outfit, sans-serif',
          fontWeight: '500',
          letterSpacing: '0.5px',
          marginBottom: window.innerWidth <= 768 ? '8px' : '15px'
        }}>
          Powered by
        </div>

        {/* Text Only Logo */}
        <div style={{
          background: `url('/assets/textonly.png') center/contain no-repeat`,
          width: window.innerWidth <= 480 ? '90px' : '120px',
          height: window.innerWidth <= 480 ? '30px' : '40px',
          marginBottom: window.innerWidth <= 768 ? '10px' : '20px',
          opacity: '0.8',
          filter: 'drop-shadow(0 5px 15px rgba(0, 0, 0, 0.1))'
        }}>
        </div>

        {/* Animated Typing Text */}
        <div style={{
          textAlign: 'center',
          marginBottom: window.innerWidth <= 768 ? '15px' : '30px',
          height: window.innerWidth <= 480 ? '40px' : '60px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            color: '#4A5568',
            fontSize: window.innerWidth <= 480 ? '12px' : window.innerWidth <= 768 ? '14px' : '18px',
            fontFamily: 'Outfit, sans-serif',
            fontWeight: '500',
            borderRight: '2px solid #F04915',
            paddingRight: '5px',
            animation: 'typing 4s steps(40) infinite, blink 1s infinite',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            maxWidth: window.innerWidth <= 480 ? '280px' : '350px'
          }}>
            The Smart University Management System
          </div>
        </div>

        {/* Decorative Elements */}
        <div style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          width: '100px',
          height: '100px',
          background: 'rgba(240, 73, 21, 0.06)',
          borderRadius: '50%',
          filter: 'blur(40px)'
        }}>
        </div>
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: '20px',
          width: '80px',
          height: '80px',
          background: 'rgba(240, 73, 21, 0.05)',
          borderRadius: '50%',
          filter: 'blur(30px)'
        }}>
        </div>
        
        {/* Floating Elements Left Side */}
        <div style={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: '10px',
          height: '10px',
          background: '#F04915',
          borderRadius: '50%',
          animation: 'float 6s ease-in-out infinite',
          opacity: '0.5'
        }}>
        </div>
        <div style={{
          position: 'absolute',
          top: '60%',
          right: '15%',
          width: '14px',
          height: '14px',
          background: '#E53E3E',
          borderRadius: '50%',
          animation: 'float 4s ease-in-out infinite 1.5s',
          opacity: '0.4'
        }}>
        </div>
        <div style={{
          position: 'absolute',
          top: '80%',
          left: '25%',
          width: '8px',
          height: '8px',
          background: '#F04915',
          borderRadius: '50%',
          animation: 'float 5s ease-in-out infinite 3s',
          opacity: '0.6'
        }}>
        </div>
        
        {/* Geometric Shapes Left Side */}
        <div style={{
          position: 'absolute',
          top: '35%',
          left: '5%',
          width: '18px',
          height: '18px',
          border: '2px solid rgba(240, 73, 21, 0.4)',
          borderRadius: '4px',
          animation: 'rotate 10s linear infinite',
          opacity: '0.3'
        }}>
        </div>
        
        {/* Additional Floating Elements */}
        <div style={{
          position: 'absolute',
          top: '10%',
          right: '8%',
          width: '12px',
          height: '12px',
          background: 'linear-gradient(45deg, #F04915, #E53E3E)',
          borderRadius: '50%',
          animation: 'float 7s ease-in-out infinite 2s',
          opacity: '0.5'
        }}>
        </div>
        <div style={{
          position: 'absolute',
          bottom: '15%',
          right: '20%',
          width: '16px',
          height: '16px',
          border: '2px solid rgba(240, 73, 21, 0.5)',
          borderRadius: '50%',
          animation: 'pulse 4s ease-in-out infinite 1s',
          opacity: '0.4'
        }}>
        </div>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '15%',
          width: '10px',
          height: '10px',
          background: '#E53E3E',
          transform: 'rotate(45deg)',
          animation: 'float 6s ease-in-out infinite 3s',
          opacity: '0.3'
        }}>
        </div>
        
        {/* Bouncing Elements */}
        <div style={{
          position: 'absolute',
          bottom: '25%',
          left: '8%',
          width: '6px',
          height: '6px',
          background: '#F04915',
          borderRadius: '50%',
          animation: 'bounce 2s ease-in-out infinite',
          opacity: '0.6'
        }}>
        </div>
        <div style={{
          position: 'absolute',
          top: '70%',
          right: '12%',
          width: '8px',
          height: '8px',
          background: '#E53E3E',
          borderRadius: '50%',
          animation: 'bounce 3s ease-in-out infinite 1s',
          opacity: '0.4'
        }}>
        </div>
      </div>

      {/* Right Side - Sign In Form */}
      <div style={{
        flex: window.innerWidth <= 768 ? 'none' : '1',
        minHeight: window.innerWidth <= 768 ? '65vh' : '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: window.innerWidth <= 768 ? '20px 15px 40px 15px' : '40px 20px',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Decorative Elements for Right Side */}
        <div style={{
          position: 'absolute',
          top: '30px',
          left: '30px',
          width: '80px',
          height: '80px',
          background: 'rgba(240, 73, 21, 0.05)',
          borderRadius: '50%',
          filter: 'blur(30px)'
        }}>
        </div>
        <div style={{
          position: 'absolute',
          bottom: '30px',
          right: '30px',
          width: '100px',
          height: '100px',
          background: 'rgba(240, 73, 21, 0.06)',
          borderRadius: '50%',
          filter: 'blur(40px)'
        }}>
        </div>
        
        {/* Floating Animated Elements */}
        <div style={{
          position: 'absolute',
          top: '15%',
          right: '10%',
          width: '12px',
          height: '12px',
          background: '#F04915',
          borderRadius: '50%',
          animation: 'float 4s ease-in-out infinite',
          opacity: '0.6'
        }}>
        </div>
        <div style={{
          position: 'absolute',
          top: '70%',
          left: '15%',
          width: '8px',
          height: '8px',
          background: '#E53E3E',
          borderRadius: '50%',
          animation: 'float 3s ease-in-out infinite 1s',
          opacity: '0.4'
        }}>
        </div>
        <div style={{
          position: 'absolute',
          top: '45%',
          right: '5%',
          width: '6px',
          height: '6px',
          background: '#F04915',
          borderRadius: '50%',
          animation: 'float 5s ease-in-out infinite 2s',
          opacity: '0.5'
        }}>
        </div>
        
        {/* Animated Geometric Shapes */}
        <div style={{
          position: 'absolute',
          top: '25%',
          left: '8%',
          width: '20px',
          height: '20px',
          border: '2px solid rgba(240, 73, 21, 0.3)',
          borderRadius: '4px',
          animation: 'rotate 8s linear infinite',
          opacity: '0.4'
        }}>
        </div>
        <div style={{
          position: 'absolute',
          bottom: '20%',
          right: '12%',
          width: '16px',
          height: '16px',
          border: '2px solid rgba(240, 73, 21, 0.4)',
          borderRadius: '50%',
          animation: 'pulse 3s ease-in-out infinite',
          opacity: '0.3'
        }}>
        </div>
        
        {/* Additional Right Side Animations */}
        <div style={{
          position: 'absolute',
          top: '60%',
          right: '8%',
          width: '14px',
          height: '14px',
          background: 'linear-gradient(45deg, #F04915, #E53E3E)',
          borderRadius: '50%',
          animation: 'float 5s ease-in-out infinite 1s',
          opacity: '0.4'
        }}>
        </div>
        <div style={{
          position: 'absolute',
          top: '10%',
          left: '12%',
          width: '12px',
          height: '12px',
          background: '#E53E3E',
          transform: 'rotate(45deg)',
          animation: 'rotate 12s linear infinite',
          opacity: '0.3'
        }}>
        </div>
        <div style={{
          position: 'absolute',
          bottom: '35%',
          left: '5%',
          width: '18px',
          height: '18px',
          border: '2px solid rgba(240, 73, 21, 0.4)',
          animation: 'pulse 6s ease-in-out infinite 2s',
          opacity: '0.3'
        }}>
        </div>
        <div style={{
          position: 'absolute',
          top: '35%',
          right: '15%',
          width: '8px',
          height: '8px',
          background: '#F04915',
          borderRadius: '50%',
          animation: 'float 4s ease-in-out infinite 3s',
          opacity: '0.5'
        }}>
        </div>
        <div style={{
          width: '100%',
          maxWidth: window.innerWidth <= 480 ? '320px' : window.innerWidth <= 768 ? '400px' : '450px',
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '20px',
          padding: window.innerWidth <= 480 ? '25px 20px' : window.innerWidth <= 768 ? '30px 25px' : '40px 30px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          position: 'relative',
          animation: 'float 6s ease-in-out infinite'
        }}>
          {/* Welcome Section */}
          <div style={{
            textAlign: 'center', 
            marginBottom: window.innerWidth <= 480 ? '20px' : '25px'
          }}>
            <h2 style={{
              fontSize: window.innerWidth <= 480 ? '20px' : window.innerWidth <= 768 ? '22px' : '26px',
              fontWeight: '700',
              color: '#2D3748',
              marginBottom: '8px',
              fontFamily: 'Outfit, sans-serif',
              background: 'linear-gradient(135deg, #2D3748 0%, #4A5568 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Welcome Back
            </h2>
            <p style={{
              color: '#798AA3', 
              fontSize: window.innerWidth <= 480 ? '13px' : window.innerWidth <= 768 ? '15px' : '17px', 
              fontFamily: 'Outfit, sans-serif',
              margin: '0',
              fontWeight: '400',
              lineHeight: '1.4'
            }}>
              Sign in to continue to your dashboard
            </p>
          </div>

        {successMessage && (
          <div style={{
            padding: '12px',
            background: '#F0FFF4',
            color: '#176837',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px',
            fontFamily: 'Outfit, sans-serif'
          }}>
            {successMessage}
          </div>
        )}

        {error && (
          <div style={{
            padding: '12px',
            background: '#FFF0F0',
            color: '#CF1717',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px',
            fontFamily: 'Outfit, sans-serif'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ marginTop: '16px' }}>
          {/* Role Selection - Clickable Cards */}
          <div style={{marginBottom: window.innerWidth <= 480 ? '25px' : '30px'}}>
            <h4 style={{
              color: '#2D3748',
              fontSize: window.innerWidth <= 480 ? '14px' : '16px',
              fontFamily: 'Outfit, sans-serif',
              fontWeight: '600',
              marginBottom: window.innerWidth <= 480 ? '12px' : '15px',
              textAlign: 'center'
            }}>
              Select Your Role
            </h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth <= 480 ? 'repeat(1, 1fr)' : window.innerWidth <= 768 ? 'repeat(1, 1fr)' : 'repeat(3, 1fr)',
              gap: window.innerWidth <= 480 ? '10px' : window.innerWidth <= 768 ? '12px' : '8px'
            }}>
              {roleOptions.map((role) => (
                <div
                  key={role.key}
                  onClick={() => handleRoleSelect(role.key)}
                  style={{
                    padding: window.innerWidth <= 480 ? '16px 14px' : window.innerWidth <= 768 ? '18px 16px' : '20px 12px',
                    borderRadius: '15px',
                    border: `2px solid ${formData.role === role.key ? role.color : '#E5E7EB'}`,
                    background: formData.role === role.key 
                      ? `linear-gradient(135deg, ${role.color}15, ${role.color}08)` 
                      : 'rgba(255, 255, 255, 0.8)',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    fontFamily: 'Outfit, sans-serif',
                    boxShadow: formData.role === role.key 
                      ? `0 10px 30px ${role.color}25` 
                      : '0 4px 15px rgba(0,0,0,0.08)',
                    transform: 'translateY(0)',
                    backdropFilter: 'blur(10px)'
                  }}
                  onMouseEnter={(e) => {
                    if (formData.role !== role.key) {
                      e.target.style.transform = 'translateY(-4px)';
                      e.target.style.boxShadow = `0 15px 40px ${role.color}20`;
                      e.target.style.borderColor = role.color;
                      e.target.style.background = `linear-gradient(135deg, ${role.color}10, ${role.color}05)`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (formData.role !== role.key) {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)';
                      e.target.style.borderColor = '#E5E7EB';
                      e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                    }
                  }}
                >
                  <div style={{
                    fontSize: window.innerWidth <= 480 ? '14px' : window.innerWidth <= 768 ? '15px' : '14px',
                    fontWeight: '600',
                    color: formData.role === role.key ? role.color : '#374151'
                  }}>
                    {role.title}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{marginBottom: window.innerWidth <= 480 ? '18px' : '20px'}}>
            <label style={{
              display: 'block',
              marginBottom: window.innerWidth <= 480 ? '8px' : '10px',
              color: '#2D3748',
              fontSize: window.innerWidth <= 480 ? '14px' : window.innerWidth <= 768 ? '15px' : '16px',
              fontFamily: 'Outfit, sans-serif',
              fontWeight: '600'
            }}>
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                height: window.innerWidth <= 480 ? '48px' : window.innerWidth <= 768 ? '50px' : '52px',
                padding: window.innerWidth <= 480 ? '0 16px' : '0 18px',
                borderRadius: '12px',
                border: '2px solid #E5E7EB',
                fontSize: window.innerWidth <= 480 ? '14px' : window.innerWidth <= 768 ? '15px' : '16px',
                fontFamily: 'Outfit, sans-serif',
                background: 'rgba(255, 255, 255, 0.9)',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(10px)',
                boxSizing: 'border-box'
              }}
              placeholder="Enter your email address"
              onFocus={(e) => {
                e.target.style.borderColor = '#F04915';
                e.target.style.boxShadow = '0 0 0 3px rgba(240, 73, 21, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#E5E7EB';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
          
          <div style={{marginBottom: window.innerWidth <= 480 ? '20px' : '25px'}}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: window.innerWidth <= 480 ? '8px' : '10px',
              flexDirection: window.innerWidth <= 480 ? 'column' : 'row',
              gap: window.innerWidth <= 480 ? '6px' : '0'
            }}>
              <label style={{
                color: '#2D3748',
                fontSize: window.innerWidth <= 480 ? '14px' : window.innerWidth <= 768 ? '15px' : '16px',
                fontFamily: 'Outfit, sans-serif',
                fontWeight: '600'
              }}>
                Password
              </label>
              <Link to="/reset-password" style={{
                color: '#F04915',
                fontSize: window.innerWidth <= 480 ? '12px' : window.innerWidth <= 768 ? '13px' : '14px',
                textDecoration: 'none',
                fontFamily: 'Outfit, sans-serif',
                fontWeight: '500',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.target.style.textDecoration = 'none';
              }}>
                Forgot Password?
              </Link>
            </div>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              style={{
                width: '100%',
                height: window.innerWidth <= 480 ? '48px' : window.innerWidth <= 768 ? '50px' : '52px',
                padding: window.innerWidth <= 480 ? '0 16px' : '0 18px',
                borderRadius: '12px',
                border: '2px solid #E5E7EB',
                fontSize: window.innerWidth <= 480 ? '14px' : window.innerWidth <= 768 ? '15px' : '16px',
                fontFamily: 'Outfit, sans-serif',
                background: 'rgba(255, 255, 255, 0.9)',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(10px)',
                boxSizing: 'border-box'
              }}
              placeholder="Enter your password"
              onFocus={(e) => {
                e.target.style.borderColor = '#F04915';
                e.target.style.boxShadow = '0 0 0 3px rgba(240, 73, 21, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#E5E7EB';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
          
          <button 
            type="submit" 
            disabled={loading}
            style={{
              width: '100%',
              height: window.innerWidth <= 480 ? '50px' : window.innerWidth <= 768 ? '52px' : '56px',
              background: loading 
                ? 'linear-gradient(135deg, #CBD5E0 0%, #A0AEC0 100%)' 
                : 'linear-gradient(135deg, #F04915 0%, #E53E3E 100%)',
              border: 'none',
              borderRadius: '12px',
              color: 'white',
              fontSize: window.innerWidth <= 480 ? '15px' : window.innerWidth <= 768 ? '16px' : '18px',
              fontFamily: 'Outfit, sans-serif',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              marginBottom: window.innerWidth <= 480 ? '18px' : '20px',
              transition: 'all 0.3s ease',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              boxShadow: loading 
                ? 'none' 
                : '0 8px 25px rgba(240, 73, 21, 0.3)',
              transform: 'translateY(0)'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 12px 35px rgba(240, 73, 21, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 8px 25px rgba(240, 73, 21, 0.3)';
              }
            }}
          >
            {loading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginRight: '10px'
                }}></div>
                Signing in...
              </div>
            ) : 'Sign In'}
          </button>
        </form>        
        
        {/* Footer Section */}
        <div style={{
          borderTop: '1px solid rgba(229, 231, 235, 0.5)',
          paddingTop: '25px',
          marginTop: '10px'
        }}>
          <div style={{
            textAlign: 'center',
            fontSize: window.innerWidth <= 768 ? '13px' : '14px',
            fontFamily: 'Outfit, sans-serif',
            marginBottom: '20px',
            color: '#6B7280'
          }}>
            Are you an admin? 
            <Link to="/register" style={{
              color: '#F04915', 
              fontWeight: '600', 
              textDecoration: 'none',
              marginLeft: '5px',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.textDecoration = 'underline';
              e.target.style.color = '#E53E3E';
            }}
            onMouseLeave={(e) => {
              e.target.style.textDecoration = 'none';
              e.target.style.color = '#F04915';
            }}>
              Register here
            </Link>
          </div>

          {/* Privacy & Policy Button */}
          <div style={{
            textAlign: 'center'
          }}>
            <Link 
              to="/privacy-policy"
              style={{
                display: 'inline-block',
                padding: window.innerWidth <= 768 ? '12px 20px' : '14px 28px',
                background: 'transparent',
                border: '2px solid #F04915',
                borderRadius: '12px',
                color: '#F04915',
                fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                fontFamily: 'Outfit, sans-serif',
                fontWeight: '600',
                textDecoration: 'none',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                backdropFilter: 'blur(10px)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'linear-gradient(135deg, #F04915 0%, #E53E3E 100%)';
                e.target.style.color = 'white';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 10px 30px rgba(240, 73, 21, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = '#F04915';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              Privacy & Policy
            </Link>
          </div>
        </div>
        </div>
      </div>
      
      {/* CSS Animations */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes fadeIn {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.3; }
          50% { transform: scale(1.2); opacity: 0.6; }
        }
        
        @keyframes rotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes typing {
          0% { width: 0; }
          50% { width: 100%; }
          100% { width: 0; }
        }
        
        @keyframes blink {
          0%, 50% { border-color: transparent; }
          51%, 100% { border-color: #F04915; }
        }
        
        @keyframes slideIn {
          0% { transform: translateX(-100px); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
        
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-8px); }
          60% { transform: translateY(-4px); }
        }
        
        @media (max-width: 768px) {
          .login-container {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
};

export default Login;